import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { useTranslation } from 'react-i18next';
import { chartTokens } from '../../design-system/chartTokens';
import { fonts } from '../../design-system/tokens';
import type { BDTheme } from '../../design-system/types';
import { getLanguage } from '../../util/languages';
import { fetchByOperator } from './api';
import { categoryColor } from './format';
import type { ByOperatorResponse, Period } from './types';

const TOP = 6;
const OTHERS_COLOR = '#9aa5a8';

type Props = { operatorId: string; period: Period; darkMode?: boolean };

export const ByOperatorTab = ({ operatorId, period, darkMode }: Props) => {
  const { t, i18n } = useTranslation();
  const theme: BDTheme = darkMode ? 'dark' : 'light';
  const tokens = chartTokens[theme];
  const locale = getLanguage(i18n.language).locale;

  const [data, setData] = useState<ByOperatorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchByOperator(operatorId, { l: period })
      .then((res) => !cancelled && setData(res))
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [operatorId, period]);

  // Top categories overall (uncategorized never counts as "top"); everything else is grouped as Others
  const { series, colors, labels, hasData } = useMemo(() => {
    const operators = data?.operators || [];
    const overall = new Map<string, { name: string; requests: number }>();
    operators.forEach((o) =>
      o.categories.forEach((c) => {
        if (c.id === 'uncategorized') return;
        const cur = overall.get(c.id) || { name: c.name, requests: 0 };
        overall.set(c.id, { name: c.name, requests: cur.requests + c.requests });
      })
    );
    const top = Array.from(overall.entries()).sort((a, b) => b[1].requests - a[1].requests).slice(0, TOP);
    const topIds = new Set(top.map(([id]) => id));

    const perCategory = top.map(([id, info]) => ({
      name: info.name,
      data: operators.map((o) => o.categories.find((c) => c.id === id)?.requests || 0),
    }));
    const others = {
      name: t('dnsCategories.operators.others'),
      data: operators.map((o) => o.categories.filter((c) => !topIds.has(c.id)).reduce((sum, c) => sum + c.requests, 0)),
    };
    return {
      series: [...perCategory, others],
      colors: [...top.map(([id]) => categoryColor(id)), OTHERS_COLOR],
      labels: operators.map((o) => `${o.name} · ${t('dnsCategories.operators.requests', { count: o.requests })}`),
      hasData: operators.some((o) => o.requests > 0),
    };
  }, [data, t]);

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      stackType: '100%',
      toolbar: { show: false },
      animations: { enabled: false },
      background: 'transparent',
      foreColor: tokens.mute,
      fontFamily: fonts.sans,
    },
    theme: { mode: theme },
    plotOptions: { bar: { horizontal: true, barHeight: '55%' } },
    colors,
    dataLabels: { enabled: false },
    grid: { borderColor: tokens.border, strokeDashArray: 3 },
    legend: { position: 'bottom', labels: { colors: tokens.text2 } },
    xaxis: { categories: labels, labels: { formatter: (v: string) => `${v}%`, style: { colors: tokens.mute } } },
    yaxis: { labels: { style: { colors: tokens.text2 }, maxWidth: 260 } },
    tooltip: { shared: false, y: { formatter: (v: number) => `${Math.round(v).toLocaleString(locale)} ${t('dnsCategories.chart.requests').toLowerCase()}` } },
  };

  return (
    <section className="bd-card tm-chart dc-operators">
      <div className="tm-chart-hd">
        <h2 className="bd-h3">{t('dnsCategories.operators.title')}</h2>
        <span className="bd-meta">{t('dnsCategories.operators.subtitle')}</span>
      </div>
      {error && <div className="dc-state bd-meta" role="alert">{t('dnsCategories.operators.error')}</div>}
      {loading && !data && !error && <div className="dc-state bd-meta">{t('dnsCategories.operators.loading')}</div>}
      {data && !error && !hasData && <div className="dc-state bd-meta">{t('dnsCategories.operators.empty')}</div>}
      {data && !error && hasData && (
        <ReactApexChart key={theme} options={options} series={series} type="bar" height={140 + data.operators.length * 70} />
      )}
    </section>
  );
};
